import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { API_URL } from "@/config/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const RiderLogin = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async () => {
    if (!mobileNumber.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}disco-auth/request-otp/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile_number: mobileNumber,
        }),
      });

      if (response.ok) {
        toast.success("One-time password sent to your mobile number");
        setOtpSent(true);
      } else {
        const errorData = await response.text();
        toast.error("Failed to send OTP. Please try again.");
        console.error('OTP request failed:', errorData);
      }
    } catch (error) {
      toast.error("Network error. Please check your connection.");
      console.error('Network error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!mobileNumber.trim() || !otp.trim()) {
      toast.error("Please enter both mobile number and OTP");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}disco-auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: mobileNumber,
          password: otp,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Login successful!");
        
        // Store the auth token if provided
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        
        // Navigate to rider dashboard or home
        navigate('/rider-my-trips');
      } else {
        const errorData = await response.text();
        toast.error("Invalid credentials. Please try again.");
        console.error('Login failed:', errorData);
      }
    } catch (error) {
      toast.error("Network error. Please check your connection.");
      console.error('Network error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RiderLayout>
      <div className="min-h-fit md:min-h-screen flex items-start md:items-center justify-center p-2 pt-2 md:p-4 md:pt-10" style={{backgroundColor: '#f5f5f5'}}>
        <Card className="w-full max-w-md bg-white text-black border-gray-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-black">Login with Mobile Number</CardTitle>
            <CardDescription className="text-gray-600">
              We'll send a one-time password to your mobile number
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="mobile" className="text-black">Mobile Number</Label>
              <Input
                id="mobile"
                type="tel"
                placeholder="Enter your mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="text-base bg-white border-gray-300 text-black placeholder:text-gray-500"
              />
            </div>

            <Button 
              onClick={handleSendOtp}
              disabled={isLoading || !mobileNumber.trim()}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              size="lg"
            >
              {isLoading ? "Sending..." : "Send One-Time Password"}
            </Button>

            <div className="space-y-2">
              <Label htmlFor="otp" className="text-black">One-Time Password</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="text-base bg-white border-gray-300 text-black placeholder:text-gray-500"
                maxLength={6}
              />
            </div>

            <Button 
              onClick={handleLogin}
              disabled={isLoading || !otp.trim()}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              size="lg"
            >
              {isLoading ? "Logging in..." : "Log In"}
            </Button>

            {otpSent && (
              <p className="text-sm text-gray-600 text-center">
                OTP sent! Check your mobile phone for the verification code.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </RiderLayout>
  );
};

export default RiderLogin;